import { t } from "@lingui/macro";
import { Link, useLocation } from "react-router-dom";
import cx from "classnames";

import { useSettings } from "context/SettingsContext/SettingsContextProvider";

import { MobileSideNav } from "components/SideNav/MobileSideNav";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { ThemeToggle } from "components/ThemeToggle/ThemeToggle";

import { AppHeaderLogo } from "./AppHeaderLogo";
import { AppHeaderUser } from "./AppHeaderUser";

type Props = {
  leftContent?: React.ReactNode;
  /**
   * `usdx` renders the design-system header — blue gradient, condensed
   * wordmark, pill nav. Opt-in per page; the trade terminal uses it.
   */
  variant?: "default" | "usdx";
};

export function AppHeader({ leftContent, variant = "default" }: Props) {
  const isUsdx = variant === "usdx";
  const { setIsSettingsVisible } = useSettings();
  const { pathname } = useLocation();

  const openSettings = () => {
    setIsSettingsVisible(true);
  };

  const navItems = [
    { label: t`Referrals`, to: "/referrals", external: false },
    { label: t`Trade`, to: "/trade", external: false },
    { label: t`Swap`, to: "/swap", external: false },
    { label: t`Leaderboard`, to: "/leaderboard", external: false },
    { label: t`Staking`, to: "https://staking.usdx.live", external: true },
    { label: t`Quest Hub`, to: "/arena", external: false },
  ];

  return (
    <header
      data-qa="header"
      className={cx(
        "flex min-h-[68px] w-full items-center justify-between gap-24 px-24 py-16",
        isUsdx ? "usdx-app-header" : "border-b border-slate-800 bg-[#1a2332]"
      )}
    >
      <div className={cx("flex items-center", isUsdx ? "gap-24" : "gap-40")}>
        {leftContent ? leftContent : <AppHeaderLogo variant={variant} />}

        <nav className={cx("hidden items-center lg:flex", isUsdx ? "gap-6" : "gap-32")}>
          {navItems.map((item, index) => {
            const isActive = !item.external && (pathname === item.to || pathname.startsWith(`${item.to}/`));
            const linkClassName = isUsdx
              ? cx(
                  "whitespace-nowrap rounded-full px-15 py-8 text-14 font-medium tracking-[0.3px] transition-colors",
                  isActive ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"
                )
              : cx("text-[15px] font-normal transition-colors hover:text-white", {
                  "text-white": isActive,
                  "text-slate-400": !isActive,
                });

            if (item.external) {
              return (
                <ExternalLink
                  key={`${item.to}-${index}`}
                  href={item.to}
                  className={`${linkClassName} !no-underline`}
                >
                  {item.label}
                </ExternalLink>
              );
            }

            return (
              <Link
                key={`${item.to}-${index}`}
                to={item.to}
                className={linkClassName}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-12">
        {isUsdx && <ThemeToggle className="max-md:hidden" />}
        <AppHeaderUser
          openSettings={openSettings}
          menuToggle={
            <div className="block lg:hidden">
              <MobileSideNav />
            </div>
          }
        />
      </div>
    </header>
  );
}
